/**
 * Historial / Listado de Atenciones de Servicios
 */
import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { atencionesAPI, comprobantesAtencionAPI } from '../services/api';

const AtencionesList = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ total: 0, page: 1, totalPages: 1 });
  const [filtros, setFiltros] = useState({
    buscar: '', estado: '', fecha_desde: '', fecha_hasta: '', metodo_pago: '', es_credito: '', page: 1
  });
  const [showDetalle, setShowDetalle] = useState(null);
  const [detalleData, setDetalleData] = useState(null);
  const [showAnular, setShowAnular] = useState(null);
  const [motivoAnular, setMotivoAnular] = useState('');
  const [showAbono, setShowAbono] = useState(null);
  const [abonoData, setAbonoData] = useState(null); // [MIG-039] detalles de la atencion (items) para el selector
  const [abonoForm, setAbonoForm] = useState({ monto: '', metodo_pago: 'efectivo', notas: '', motivo: '', item_especifico_id: '' });

  // [MIG-039] abrir modal de abono cargando los detalles de la atencion para el selector
  const abrirAbono = async (id) => {
    setShowAbono(id);
    setAbonoData(null);
    setAbonoForm({ monto: '', metodo_pago: 'efectivo', notas: '', motivo: '', item_especifico_id: '' });
    try {
      const res = await atencionesAPI.getById(id);
      if (res.data.success) setAbonoData(res.data.data);
    } catch (e) {
      // silencioso, el modal funciona sin items
    }
  };

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params = { ...filtros, limit: 20 };
      Object.keys(params).forEach(k => (params[k] === '' || params[k] === null) && delete params[k]);
      const res = await atencionesAPI.getAll(params);
      if (res.data.success) {
        setItems(res.data.data);
        setPagination(res.data.pagination || { total: res.data.data.length, page: 1, totalPages: 1 });
      }
    } catch (e) {
      toast.error('Error al cargar atenciones');
    } finally {
      setLoading(false);
    }
  }, [filtros]);

  useEffect(() => { load(); }, [load]);

  const abrirDetalle = async (id) => {
    try {
      setShowDetalle(id);
      const res = await atencionesAPI.getById(id);
      if (res.data.success) setDetalleData(res.data.data);
    } catch (e) {
      toast.error('Error al cargar detalle');
    }
  };

  const abrirPDF = async (comprobanteId) => {
    try {
      const res = await comprobantesAtencionAPI.descargarPDF(comprobanteId);
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000); // libera memoria tras 1 min
    } catch (e) { toast.error('Error al abrir PDF'); }
  };

  const abrirTicket = async (comprobanteId) => {
    try {
      const res = await comprobantesAtencionAPI.descargarTicket(comprobanteId);
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000); // libera memoria tras 1 min
    } catch (e) { toast.error('Error al abrir ticket'); }
  };

  const enviarEmail = async (comprobanteId) => {
    const email = window.prompt('Email destinatario:');
    if (!email) return;
    try {
      await comprobantesAtencionAPI.enviarEmail(comprobanteId, { destinatario: email });
      toast.success(`Email enviado a ${email}`);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Error al enviar email');
    }
  };

  const handleAnular = async () => {
    if (!motivoAnular || motivoAnular.trim().length < 5) { toast.error('Motivo mínimo 5 caracteres'); return; }
    try {
      await atencionesAPI.anular(showAnular, { motivo: motivoAnular });
      toast.success('Atención anulada');
      setShowAnular(null); setMotivoAnular('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Error al anular');
    }
  };

  const handleAbono = async () => {
    if (!abonoForm.monto || parseFloat(abonoForm.monto) <= 0) { toast.error('Monto inválido'); return; }
    // [MIG-039] Justificativo obligatorio
    if (!abonoForm.motivo || abonoForm.motivo.trim().length < 15) {
      toast.error('El motivo del abono es obligatorio (mínimo 15 caracteres)');
      return;
    }
    try {
      const res = await atencionesAPI.registrarAbono(showAbono, {
        monto: parseFloat(abonoForm.monto),
        metodo_pago: abonoForm.metodo_pago,
        notas: abonoForm.notas || null,
        motivo: abonoForm.motivo.trim(),
        item_especifico_id: abonoForm.item_especifico_id || null
      });
      if (res.data.success) {
        if (res.data.data?.pagado_completo) {
          toast.success('Abono registrado. Crédito saldado.');
        } else {
          toast.success(`Abono registrado. Saldo: $${res.data.data.nuevo_saldo.toFixed(2)}`);
        }
        setShowAbono(null);
        setAbonoData(null);
        setAbonoForm({ monto: '', metodo_pago: 'efectivo', notas: '', motivo: '', item_especifico_id: '' });
        load();
        if (detalleData?.id === showAbono) abrirDetalle(showAbono);
      }
    } catch (e) {
      toast.error(e.response?.data?.message || 'Error al registrar abono');
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-start mb-4 flex-wrap gap-3">
        <div>
          <h1 className="h3 mb-1 fw-bold">
            <i className="bi bi-journal-medical me-2" style={{ color: '#0984e3' }}></i>
            Historial de Atenciones
          </h1>
          <p className="text-muted mb-0 small">Comprobantes de atención emitidos por servicios profesionales</p>
        </div>
        <a href="/pos-servicios" className="btn btn-primary"><i className="bi bi-plus-lg me-2"></i>Nueva atención</a>
      </div>

      {/* Filtros */}
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body">
          <div className="row g-2">
            <div className="col-md-4">
              <div className="input-group">
                <span className="input-group-text bg-white"><i className="bi bi-search"></i></span>
                <input className="form-control" placeholder="Código, paciente, cédula, comprobante" value={filtros.buscar} onChange={e => setFiltros({ ...filtros, buscar: e.target.value, page: 1 })} />
              </div>
            </div>
            <div className="col-md-2">
              <input type="date" className="form-control" value={filtros.fecha_desde} onChange={e => setFiltros({ ...filtros, fecha_desde: e.target.value, page: 1 })} />
            </div>
            <div className="col-md-2">
              <input type="date" className="form-control" value={filtros.fecha_hasta} onChange={e => setFiltros({ ...filtros, fecha_hasta: e.target.value, page: 1 })} />
            </div>
            <div className="col-md-2">
              <select className="form-select" value={filtros.estado} onChange={e => setFiltros({ ...filtros, estado: e.target.value, page: 1 })}>
                <option value="">Todos los estados</option>
                <option value="completada">Completada</option>
                <option value="anulada">Anulada</option>
              </select>
            </div>
            <div className="col-md-2">
              <select className="form-select" value={filtros.es_credito} onChange={e => setFiltros({ ...filtros, es_credito: e.target.value, page: 1 })}>
                <option value="">Todas</option>
                <option value="true">Solo crédito</option>
                <option value="false">Sin crédito</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Listado */}
      <div className="card border-0 shadow-sm">
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-5"><div className="spinner-border text-primary"></div></div>
          ) : items.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <i className="bi bi-inbox fs-1 mb-3 d-block"></i>
              <p>No hay atenciones con estos filtros</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover mb-0 align-middle">
                <thead className="table-light">
                  <tr>
                    <th className="ps-3">Código / Comprobante</th>
                    <th>Paciente</th>
                    <th>Fecha</th>
                    <th className="text-end">Total</th>
                    <th>Pago</th>
                    <th>Saldo</th>
                    <th>Estado</th>
                    <th className="text-end pe-3">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(a => (
                    <tr key={a.id}>
                      <td className="ps-3">
                        <div className="fw-bold">{a.codigo}</div>
                        {a.numero_orden && <div className="small fw-bold text-primary">Orden: {a.numero_orden}</div>}
                        <div className="small fw-bold" style={{ color: '#0984e3' }}>{a.comprobante_numero || '-'}</div>
                      </td>
                      <td>
                        {a.nombres || a.apellidos || a.razon_social ? (
                          <>
                            <div className="small fw-medium">{a.razon_social || `${a.nombres || ''} ${a.apellidos || ''}`.trim()}</div>
                            <div className="text-muted" style={{ fontSize: '.75rem' }}>{a.identificacion}</div>
                          </>
                        ) : <span className="text-muted small">Consumidor Final</span>}
                      </td>
                      <td className="small">{new Date(a.created_at).toLocaleString('es-EC', { dateStyle: 'short', timeStyle: 'short' })}</td>
                      <td className="text-end fw-bold">${parseFloat(a.total).toFixed(2)}</td>
                      <td><span className="badge bg-secondary-subtle text-secondary">{(a.metodo_pago || '').replace('_', ' ')}</span></td>
                      <td>
                        {a.es_credito && parseFloat(a.saldo_pendiente) > 0.01 ? (
                          <span className="fw-medium text-warning">${parseFloat(a.saldo_pendiente).toFixed(2)}</span>
                        ) : <span className="text-muted small">—</span>}
                      </td>
                      <td>
                        {a.estado === 'completada' ? (
                          a.es_credito && parseFloat(a.saldo_pendiente) > 0.01
                            ? <span className="badge bg-warning-subtle text-warning">Crédito</span>
                            : <span className="badge bg-success-subtle text-success">Pagada</span>
                        ) : <span className="badge bg-danger-subtle text-danger">Anulada</span>}
                      </td>
                      <td className="text-end pe-3">
                        <div className="btn-group btn-group-sm">
                          <button className="btn btn-outline-dark" title="Detalle" onClick={() => abrirDetalle(a.id)}><i className="bi bi-eye"></i></button>
                          <button className="btn btn-outline-primary" title="PDF" onClick={() => abrirPDF(a.comprobante_id)} disabled={!a.comprobante_id}><i className="bi bi-file-earmark-pdf"></i></button>
                          <button className="btn btn-outline-secondary" title="Ticket" onClick={() => abrirTicket(a.comprobante_id)} disabled={!a.comprobante_id}><i className="bi bi-receipt"></i></button>
                          <button className="btn btn-outline-info" title="Email" onClick={() => enviarEmail(a.comprobante_id)} disabled={!a.comprobante_id}><i className="bi bi-envelope"></i></button>
                          {a.es_credito && parseFloat(a.saldo_pendiente) > 0.01 && a.estado === 'completada' && (
                            <button className="btn btn-outline-warning" title="Registrar abono" onClick={() => abrirAbono(a.id)}><i className="bi bi-cash-coin"></i></button>
                          )}
                          {a.estado === 'completada' && (
                            <button className="btn btn-outline-danger" title="Anular" onClick={() => setShowAnular(a.id)}><i className="bi bi-x-circle"></i></button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        {!loading && pagination.totalPages > 1 && (
          <div className="card-footer bg-white d-flex justify-content-between align-items-center">
            <span className="small text-muted">Total: {pagination.total} atenciones</span>
            <div className="btn-group btn-group-sm">
              <button className="btn btn-outline-secondary" disabled={filtros.page <= 1} onClick={() => setFiltros({ ...filtros, page: filtros.page - 1 })}><i className="bi bi-chevron-left"></i></button>
              <span className="btn btn-outline-secondary disabled">Página {filtros.page} de {pagination.totalPages}</span>
              <button className="btn btn-outline-secondary" disabled={filtros.page >= pagination.totalPages} onClick={() => setFiltros({ ...filtros, page: filtros.page + 1 })}><i className="bi bi-chevron-right"></i></button>
            </div>
          </div>
        )}
      </div>

      {/* Modal detalle */}
      {showDetalle && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.55)' }} onClick={(e) => e.target === e.currentTarget && setShowDetalle(null)}>
          <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content border-0 shadow-lg">
              <div className="modal-header" style={{ background: 'linear-gradient(135deg, #0984e3 0%, #6c5ce7 100%)', color: 'white' }}>
                <h5 className="modal-title fw-bold"><i className="bi bi-file-text me-2"></i>Detalle · {detalleData?.codigo || showDetalle}</h5>
                <button className="btn-close btn-close-white" onClick={() => { setShowDetalle(null); setDetalleData(null); }}></button>
              </div>
              <div className="modal-body">
                {!detalleData ? (
                  <div className="text-center py-4"><div className="spinner-border text-primary"></div></div>
                ) : (
                  <div>
                    <div className="row g-3 mb-3">
                      <div className="col-md-6">
                        <div className="small text-muted">Paciente</div>
                        <div className="fw-medium">{detalleData.razon_social || `${detalleData.nombres || ''} ${detalleData.apellidos || ''}`.trim() || 'Consumidor Final'}</div>
                        {detalleData.identificacion && <div className="small text-muted">{detalleData.identificacion}</div>}
                      </div>
                      <div className="col-md-6">
                        <div className="small text-muted">Comprobante</div>
                        <div className="fw-bold" style={{ color: '#0984e3' }}>{detalleData.comprobante_numero || '-'}</div>
                      </div>
                      <div className="col-md-6">
                        <div className="small text-muted">Optometrista</div>
                        <div>{detalleData.optometrista_nombre || '—'}</div>
                      </div>
                      <div className="col-md-6">
                        <div className="small text-muted">Historia clínica</div>
                        <div>{detalleData.hc_codigo || '—'}</div>
                      </div>
                    </div>

                    <h6 className="fw-bold mt-3">Servicios</h6>
                    <table className="table table-sm">
                      <thead className="table-light">
                        <tr><th>Servicio</th><th className="text-end">Cantidad</th><th className="text-end">Precio</th><th className="text-end">Total</th></tr>
                      </thead>
                      <tbody>
                        {detalleData.detalles?.map(d => (
                          <tr key={d.id}>
                            <td>
                              <div className="small fw-medium">{d.servicio_nombre}</div>
                              <div className="text-muted" style={{ fontSize: '.7rem' }}>{d.servicio_codigo} · {d.categoria}</div>
                            </td>
                            <td className="text-end">{d.cantidad}</td>
                            <td className="text-end">${parseFloat(d.precio_unitario).toFixed(2)}</td>
                            <td className="text-end fw-bold">${parseFloat(d.total).toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="table-light">
                        <tr><td colSpan="3" className="text-end">Subtotal:</td><td className="text-end">${parseFloat(detalleData.subtotal).toFixed(2)}</td></tr>
                        {parseFloat(detalleData.descuento_total) > 0 && <tr><td colSpan="3" className="text-end">Descuento:</td><td className="text-end text-danger">-${parseFloat(detalleData.descuento_total).toFixed(2)}</td></tr>}
                        <tr><td colSpan="3" className="text-end fw-bold">TOTAL:</td><td className="text-end fw-bold fs-5" style={{ color: '#0984e3' }}>${parseFloat(detalleData.total).toFixed(2)}</td></tr>
                      </tfoot>
                    </table>

                    {detalleData.es_credito && (
                      <div className="alert alert-warning">
                        <div className="d-flex justify-content-between">
                          <span>Abonado:</span><span className="fw-medium">${parseFloat(detalleData.monto_abonado).toFixed(2)}</span>
                        </div>
                        <div className="d-flex justify-content-between">
                          <span>Saldo pendiente:</span><span className="fw-bold">${parseFloat(detalleData.saldo_pendiente).toFixed(2)}</span>
                        </div>
                      </div>
                    )}

                    {detalleData.abonos?.length > 0 && (
                      <>
                        <h6 className="fw-bold mt-3">Abonos ({detalleData.abonos.length})</h6>
                        <table className="table table-sm">
                          <thead><tr><th>Fecha</th><th>Método</th><th className="text-end">Monto</th><th>Motivo</th><th>Usuario</th></tr></thead>
                          <tbody>
                            {detalleData.abonos.map(a => (
                              <tr key={a.id}>
                                <td className="small">{new Date(a.created_at).toLocaleString('es-EC', { dateStyle: 'short', timeStyle: 'short' })}</td>
                                <td><span className="badge bg-secondary-subtle text-secondary">{a.metodo_pago}</span></td>
                                <td className="text-end fw-medium">${parseFloat(a.monto).toFixed(2)}</td>
                                <td className="small" style={{ maxWidth: '200px' }} title={a.motivo}>
                                  {a.motivo
                                    ? (a.motivo.length > 40 ? a.motivo.substring(0, 40) + '…' : a.motivo)
                                    : <span className="text-muted">—</span>}
                                </td>
                                <td className="small text-muted">{a.usuario_nombre || '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </>
                    )}

                    {detalleData.notas && (
                      <div className="mt-3">
                        <div className="small text-muted mb-1">Notas</div>
                        <div className="p-2 bg-light rounded small">{detalleData.notas}</div>
                      </div>
                    )}

                    {detalleData.estado === 'anulada' && (
                      <div className="alert alert-danger mt-3">
                        <strong>ANULADA</strong> el {new Date(detalleData.fecha_anulacion).toLocaleString('es-EC')}<br />
                        Motivo: {detalleData.motivo_anulacion}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                {detalleData?.comprobante_id && (
                  <>
                    <button className="btn btn-outline-primary" onClick={() => abrirPDF(detalleData.comprobante_id)}><i className="bi bi-file-earmark-pdf me-1"></i>PDF</button>
                    <button className="btn btn-outline-secondary" onClick={() => abrirTicket(detalleData.comprobante_id)}><i className="bi bi-receipt me-1"></i>Ticket</button>
                    <button className="btn btn-outline-info" onClick={() => enviarEmail(detalleData.comprobante_id)}><i className="bi bi-envelope me-1"></i>Email</button>
                  </>
                )}
                <button className="btn btn-secondary" onClick={() => { setShowDetalle(null); setDetalleData(null); }}>Cerrar</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal anular */}
      {showAnular && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.55)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header bg-danger text-white">
                <h5 className="modal-title"><i className="bi bi-exclamation-triangle me-2"></i>Anular atención</h5>
                <button className="btn-close btn-close-white" onClick={() => { setShowAnular(null); setMotivoAnular(''); }}></button>
              </div>
              <div className="modal-body">
                <p className="small">Esta acción es reversible solo mediante corrección contable. Indica el motivo:</p>
                <textarea className="form-control" rows={3} minLength={5} placeholder="Motivo de anulación (mínimo 5 caracteres)" value={motivoAnular} onChange={e => setMotivoAnular(e.target.value)}></textarea>
              </div>
              <div className="modal-footer">
                <button className="btn btn-light" onClick={() => { setShowAnular(null); setMotivoAnular(''); }}>Cancelar</button>
                <button className="btn btn-danger" onClick={handleAnular}><i className="bi bi-x-circle me-1"></i>Anular</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal abono */}
      {showAbono && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.55)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header" style={{ background: '#fdcb6e' }}>
                <h5 className="modal-title"><i className="bi bi-cash-coin me-2"></i>Registrar abono</h5>
                <button className="btn-close" onClick={() => setShowAbono(null)}></button>
              </div>
              <div className="modal-body">
                <div className="row g-2">
                  <div className="col-12">
                    <label className="small text-muted">Monto del abono</label>
                    <div className="input-group">
                      <span className="input-group-text">$</span>
                      <input
                        type="number"
                        min="0.01"
                        step="0.01"
                        className="form-control"
                        value={abonoForm.monto}
                        onWheel={e => e.currentTarget.blur()}
                        onKeyDown={e => { if (e.key === 'ArrowUp' || e.key === 'ArrowDown') e.preventDefault(); }}
                        onChange={e => setAbonoForm({ ...abonoForm, monto: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="col-12">
                    <label className="small text-muted">Método de pago</label>
                    <select className="form-select" value={abonoForm.metodo_pago} onChange={e => setAbonoForm({ ...abonoForm, metodo_pago: e.target.value })}>
                      <option value="efectivo">Efectivo</option>
                      <option value="tarjeta">Tarjeta</option>
                      <option value="transferencia">Transferencia</option>
                    </select>
                  </div>
                  {/* [MIG-039] Items de la atencion + selector */}
                  {abonoData?.detalles?.length > 0 && (
                    <div className="col-12">
                      <label className="small text-muted fw-bold">Servicios de esta atención</label>
                      <div className="border rounded p-2 mb-2 bg-light" style={{ maxHeight: '120px', overflowY: 'auto' }}>
                        {abonoData.detalles.map(it => (
                          <div key={it.id} className="small d-flex justify-content-between border-bottom py-1">
                            <span>{it.servicio_nombre} <span className="text-muted">× {it.cantidad}</span></span>
                            <span className="fw-bold">${parseFloat(it.total).toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                      <label className="small text-muted">Aplicar abono a:</label>
                      <select className="form-select form-select-sm"
                        value={abonoForm.item_especifico_id}
                        onChange={e => {
                          const itemId = e.target.value;
                          const updates = { item_especifico_id: itemId };
                          // [UX 2026-05-07] Auto-sugerir motivo si está vacío o sub-15 chars
                          const motivoActual = (abonoForm.motivo || '').trim();
                          if (motivoActual.length < 15) {
                            if (itemId) {
                              const det = abonoData.detalles.find(it => String(it.id) === String(itemId));
                              if (det) {
                                updates.motivo = `Abono sobre ${det.servicio_nombre} ($${parseFloat(det.total).toFixed(2)})`;
                              }
                            } else if (abonoData?.codigo) {
                              updates.motivo = `Abono general sobre atención ${abonoData.codigo}`;
                            }
                          }
                          setAbonoForm({ ...abonoForm, ...updates });
                        }}>
                        <option value="">Abono general (toda la atención)</option>
                        {abonoData.detalles.map(it => (
                          <option key={it.id} value={it.id}>
                            {it.servicio_nombre} (${parseFloat(it.total).toFixed(2)})
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  <div className="col-12">
                    <label className="small text-muted">Notas (opcional)</label>
                    <input className="form-control" value={abonoForm.notas} onChange={e => setAbonoForm({ ...abonoForm, notas: e.target.value })} />
                  </div>
                  {/* [MIG-039] Motivo OBLIGATORIO mín 15 chars */}
                  <div className="col-12">
                    <label className="small text-muted fw-bold">
                      Motivo / Justificativo <span className="text-danger">*</span>
                    </label>
                    <textarea className="form-control"
                      rows={2}
                      required
                      minLength={15}
                      maxLength={500}
                      placeholder="Se completará automáticamente al elegir un servicio arriba. Edítalo si quieres agregar detalle."
                      value={abonoForm.motivo}
                      onChange={e => setAbonoForm({ ...abonoForm, motivo: e.target.value })}></textarea>
                    <small className={(abonoForm.motivo?.trim().length || 0) < 15 ? 'text-danger' : 'text-success'}>
                      {(abonoForm.motivo?.trim().length || 0)}/15 caracteres mínimo
                    </small>
                    {(abonoForm.motivo?.trim().length || 0) < 15 && (
                      <div className="text-muted small mt-1">
                        <i className="bi bi-lightbulb me-1"></i>
                        Tip: selecciona el servicio arriba y el motivo se llenará solo.
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-light" onClick={() => { setShowAbono(null); setAbonoData(null); }}>Cancelar</button>
                <button className="btn btn-warning fw-bold" onClick={handleAbono}
                  disabled={!abonoForm.monto || (abonoForm.motivo?.trim().length || 0) < 15}>
                  <i className="bi bi-check-lg me-1"></i>Registrar abono
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AtencionesList;
